/**
 * CrashMarkerService - Détecte les crashes renderer/WebView
 * Permet de récupérer un rapport même quand ErrorBoundary ne peut pas s'afficher
 * 
 * Fonctionne via heartbeat + markers en localStorage pour survivre aux crashes
 * 
 * @module CrashMarkerService
 */

import { logger } from '@/services/logger';

// Clés localStorage
const KEYS = {
  HEARTBEAT: 'koken.crashMarker.heartbeat',
  LAST_ROUTE: 'koken.crashMarker.lastRoute',
  LAST_ACTION: 'koken.crashMarker.lastAction',
  CLEAN_SHUTDOWN: 'koken.crashMarker.cleanShutdown',
  SESSION_START: 'koken.crashMarker.sessionStart',
  LAST_ERROR: 'koken.crashMarker.lastError',
  CRASH_DETECTED: 'koken.crashMarker.crashDetected',
  CRASH_CONTEXT: 'koken.crashMarker.crashContext',
  BREADCRUMBS: 'koken.crashMarker.breadcrumbs',
  MEMORY_SAMPLES: 'koken.crashMarker.memorySamples',
  INVALIDATION_COUNT: 'koken.crashMarker.invalidationCount',
};

// Types - Extended for better crash diagnostics
export type CrashAction = 
  | 'none' 
  | 'logout' 
  | 'navigation' 
  | 'api_call' 
  | 'render' 
  | 'role_switch' 
  | 'auth_change'
  | 'realtime_event'
  | 'query_invalidate'
  | 'shop_purchase'
  | 'shop_load';

export interface ActionBreadcrumb {
  action: CrashAction;
  detail?: string;
  timestamp: number;
  memoryMB?: number;
}

export interface CrashContext {
  action: CrashAction;
  route: string;
  timestamp: string;
  sessionDuration: number; // en secondes
  lastError?: string;
  userAgent: string;
  appVersion: string;
  breadcrumbs?: ActionBreadcrumb[];
  // ✅ NEW: Enhanced diagnostics
  memoryMB?: number;
  peakMemoryMB?: number;
  invalidationCount?: number;
  queriesInFlight?: number;
  // ✅ User identification for crash reports
  userId?: string;
  userEmail?: string;
}

// Configuration
const HEARTBEAT_INTERVAL = 2000; // 2 secondes
const CRASH_THRESHOLD = 15000; // 15 secondes sans heartbeat = crash potentiel
const MAX_BREADCRUMBS = 30; // ✅ Increased from 5 to 30 for better diagnostics

let heartbeatInterval: ReturnType<typeof setInterval> | null = null;

/**
 * CrashMarkerService - Singleton pour la détection de crashes
 */
export const crashMarkerService = {
  /**
   * Initialise le service au démarrage de l'app
   * Doit être appelé très tôt dans le boot
   */
  initialize(): void {
    try {
      // Vérifier d'abord s'il y a eu un crash au boot précédent
      const hadCrash = this.detectCrash();
      
      if (hadCrash) {
        logger.warn('[CrashMarker] 🔴 Crash détecté au boot précédent');
        // On ne clear pas ici, PostCrashRecovery le fera après affichage
      }

      // Marquer le début de session
      localStorage.setItem(KEYS.SESSION_START, new Date().toISOString());
      localStorage.setItem(KEYS.CLEAN_SHUTDOWN, 'false');
      localStorage.setItem(KEYS.LAST_ACTION, 'none');
      localStorage.removeItem(KEYS.LAST_ERROR);

      // Démarrer le heartbeat
      this.startHeartbeat();

      // Écouter beforeunload pour marquer un shutdown propre
      window.addEventListener('beforeunload', this.handleBeforeUnload);
      
      // ✅ FIX FAUX POSITIFS: Écouter visibilitychange pour détecter les fermetures intentionnelles
      // Sur mobile, beforeunload n'est pas fiable, mais visibilitychange l'est
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      
      // Écouter les erreurs globales pour les enregistrer
      window.addEventListener('error', this.handleGlobalError);
      window.addEventListener('unhandledrejection', this.handleUnhandledRejection);

      logger.debug('[CrashMarker] ✅ Initialisé');
    } catch (e) {
      logger.warn('[CrashMarker] Échec initialisation:', e);
    }
  },

  /**
   * Démarre le heartbeat régulier
   */
  startHeartbeat(): void {
    if (heartbeatInterval) return;

    this.updateHeartbeat();
    heartbeatInterval = setInterval(() => {
      this.updateHeartbeat();
    }, HEARTBEAT_INTERVAL);
  },

  /**
   * Arrête le heartbeat
   */
  stopHeartbeat(): void {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }
  },

  /**
   * Met à jour le timestamp du heartbeat
   */
  updateHeartbeat(): void {
    try {
      localStorage.setItem(KEYS.HEARTBEAT, String(Date.now()));
    } catch {
      // Ignore
    }
  },

  /**
   * Met à jour la route actuelle
   */
  setRoute(route: string): void {
    try {
      localStorage.setItem(KEYS.LAST_ROUTE, route);
    } catch {
      // Ignore
    }
  },

  /**
   * Met à jour l'action en cours
   */
  setAction(action: CrashAction): void {
    try {
      localStorage.setItem(KEYS.LAST_ACTION, action);
    } catch {
      // Ignore
    }
  },

  /**
   * Ajoute un breadcrumb à l'historique (garde les 30 derniers)
   * ✅ Enhanced: Now captures memory state for each breadcrumb
   */
  addBreadcrumb(action: CrashAction, detail?: string): void {
    try {
      const breadcrumbs = this.getBreadcrumbs();
      const memoryMB = this.getMemoryMB();
      
      breadcrumbs.push({ 
        action, 
        detail, 
        timestamp: Date.now(),
        memoryMB 
      });
      
      // Garder les 30 derniers (circular buffer)
      while (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.shift();
      localStorage.setItem(KEYS.BREADCRUMBS, JSON.stringify(breadcrumbs));
      
      // Track peak memory
      if (memoryMB) {
        const currentPeak = this.getPeakMemory();
        if (memoryMB > currentPeak) {
          localStorage.setItem(KEYS.MEMORY_SAMPLES, String(memoryMB));
        }
      }
    } catch {
      // Ignore
    }
  },

  /**
   * Get current memory usage in MB (Chrome only)
   */
  getMemoryMB(): number | undefined {
    try {
      // @ts-expect-error - performance.memory is Chrome-only
      const memory = performance.memory;
      if (memory && typeof memory.usedJSHeapSize === 'number') {
        return Math.round(memory.usedJSHeapSize / (1024 * 1024));
      }
    } catch {
      // Not available
    }
    return undefined;
  },

  /**
   * Get peak memory recorded this session
   */
  getPeakMemory(): number {
    try {
      return parseInt(localStorage.getItem(KEYS.MEMORY_SAMPLES) || '0', 10);
    } catch {
      return 0;
    }
  },

  /**
   * Track query invalidation (helps detect "invalidate storms")
   */
  trackInvalidation(queryKey?: string): void {
    try {
      const count = parseInt(localStorage.getItem(KEYS.INVALIDATION_COUNT) || '0', 10);
      localStorage.setItem(KEYS.INVALIDATION_COUNT, String(count + 1));
      this.addBreadcrumb('query_invalidate', queryKey);
    } catch {
      // Ignore
    }
  },

  /**
   * Get invalidation count for this session
   */
  getInvalidationCount(): number {
    try {
      return parseInt(localStorage.getItem(KEYS.INVALIDATION_COUNT) || '0', 10);
    } catch {
      return 0;
    }
  },

  /**
   * Track shop-specific actions for debugging memory crashes
   */
  trackShopAction(action: 'load' | 'purchase' | 'realtime_event', detail?: string): void {
    const actionType: CrashAction = action === 'load' ? 'shop_load' 
      : action === 'purchase' ? 'shop_purchase' 
      : 'realtime_event';
    this.addBreadcrumb(actionType, detail);
    this.setAction(actionType);
  },

  /**
   * Récupère les breadcrumbs
   */
  getBreadcrumbs(): ActionBreadcrumb[] {
    try {
      const stored = localStorage.getItem(KEYS.BREADCRUMBS);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  },

  /**
   * Track une navigation
   */
  trackNavigation(from: string, to: string): void {
    // ✅ FIX: Si navigation vers /login, c'est probablement une expiration de session, pas un crash
    if (to === '/login' || to.startsWith('/login')) {
      this.markCleanShutdown();
      try { localStorage.removeItem(KEYS.LAST_ERROR); } catch { /* ignore */ }
      logger.debug('[CrashMarker] Navigation vers /login détectée → cleanShutdown + clear LAST_ERROR');
    } else {
      // ✅ FIX FAUX POSITIFS: Navigation interne = "maybe" pour éviter faux positif si refresh/reload
      try {
        localStorage.setItem(KEYS.CLEAN_SHUTDOWN, 'maybe');
      } catch {
        // Ignore
      }
    }
    this.addBreadcrumb('navigation', `${from} → ${to}`);
    this.setAction('navigation');
  },

  /**
   * Track un appel API
   */
  trackApiCall(endpoint: string): void {
    this.addBreadcrumb('api_call', endpoint);
    this.setAction('api_call');
  },

  /**
   * Track un changement de rôle
   */
  trackRoleSwitch(role: string): void {
    this.addBreadcrumb('role_switch', role);
    this.setAction('role_switch');
  },

  /**
   * Track un changement d'auth
   */
  trackAuthChange(type: 'login' | 'logout' | 'session_refresh'): void {
    this.addBreadcrumb('auth_change', type);
    this.setAction(type === 'logout' ? 'logout' : 'auth_change');
  },

  /**
   * Marque le début du logout (action critique)
   */
  markLogoutStart(): void {
    this.trackAuthChange('logout');
    logger.debug('[CrashMarker] 📤 Logout marqué');
  },

  /**
   * Marque un shutdown propre
   */
  markCleanShutdown(): void {
    try {
      localStorage.setItem(KEYS.CLEAN_SHUTDOWN, 'true');
      this.stopHeartbeat();
      logger.debug('[CrashMarker] ✅ Clean shutdown marqué');
    } catch {
      // Ignore
    }
  },

  /**
   * Handler pour beforeunload
   */
  handleBeforeUnload: () => {
    try {
      // ✅ FIX FAUX POSITIFS: Marquer comme "maybe" pour éviter les faux positifs sur refresh/reload
      // beforeunload se déclenche lors des refreshes, des navigations externes, etc.
      localStorage.setItem(KEYS.CLEAN_SHUTDOWN, 'maybe');
      localStorage.setItem(KEYS.HEARTBEAT, String(Date.now()));
    } catch {
      // Ignore
    }
  },

  /**
   * Handler pour visibilitychange - réduit les faux positifs sur mobile
   * Quand l'app passe en arrière-plan ou se ferme normalement
   */
  handleVisibilityChange: () => {
    try {
      if (document.visibilityState === 'hidden') {
        // L'app passe en arrière-plan ou se ferme
        // Marquer comme "probablement fermeture intentionnelle" (pas un crash)
        localStorage.setItem(KEYS.CLEAN_SHUTDOWN, 'maybe');
        localStorage.setItem(KEYS.HEARTBEAT, String(Date.now()));
      } else if (document.visibilityState === 'visible') {
        // L'app revient au premier plan - session continue
        localStorage.setItem(KEYS.CLEAN_SHUTDOWN, 'false');
      }
    } catch {
      // Ignore
    }
  },

  /**
   * Handler pour les erreurs globales
   */
  handleGlobalError: (event: ErrorEvent) => {
    try {
      const errorInfo = {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(KEYS.LAST_ERROR, JSON.stringify(errorInfo));
    } catch {
      // Ignore
    }
  },

  /**
   * Handler pour les promesses rejetées non gérées
   */
  handleUnhandledRejection: (event: PromiseRejectionEvent) => {
    // ✅ Toujours empêcher le navigateur de traiter la rejection comme fatale
    event.preventDefault();

    try {
      const reason = event.reason;
      const message = reason?.message || String(reason);

      // ✅ Ignorer les AbortError (annulations normales de fetch lors de la navigation)
      if (reason?.name === 'AbortError' || message.includes('signal is aborted') || message.includes('The user aborted a request')) {
        return;
      }
      
      // ✅ Ignorer les erreurs d'auth Supabase normales (expiration de session, refresh de token)
      const AUTH_ERROR_PATTERNS = [
        'JWT expired', 'Invalid Refresh Token', 'refresh_token_not_found',
        'AuthSessionMissingError', 'PGRST301', 'token is expired',
        'invalid claim: missing sub claim', 'Auth session missing'
      ];
      if (AUTH_ERROR_PATTERNS.some(pattern => message.includes(pattern))) {
        logger.debug('[CrashMarker] Auth error ignorée (non-critique):', message.substring(0, 80));
        return;
      }

      const errorInfo = {
        message,
        stack: reason?.stack,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(KEYS.LAST_ERROR, JSON.stringify(errorInfo));
    } catch {
      // Ignore
    }
  },

  /**
   * Actions considérées comme non-critiques : une fermeture "maybe" sur ces actions
   * ne doit PAS déclencher un crash (refresh normal, fermeture d'onglet, navigation)
   */
  NON_CRITICAL_ACTIONS: ['none', 'shop_load', 'navigation', 'api_call', 'realtime_event', 'query_invalidate'] as string[],

  /**
   * Détecte si un crash a eu lieu au boot précédent
   */
  detectCrash(): boolean {
    try {
      // Si crash déjà détecté et non traité.
      //
      // ⚠️ Ce raccourci court-circuitait TOUS les garde-fous anti-faux-positifs
      // ajoutés plus bas en janvier et février 2026, et il ne rejoue pas
      // saveCrashContext() : le contexte périmé était réémis tel quel, avec son
      // ancienne action, son ancienne route et son lastError vide. C'est le profil
      // exact des rapports « renderer_crash » à stack vide observés jusqu'au
      // 15/06/2026, trois mois après les correctifs censés les avoir éteints.
      // On applique donc au contexte mémorisé le même filtre qu'à une détection
      // fraîche : une action non critique sans erreur JS n'est pas un crash.
      const alreadyDetected = localStorage.getItem(KEYS.CRASH_DETECTED) === 'true';
      if (alreadyDetected) {
        const memorized = this.getCrashContext();
        const memorizedAction = memorized?.action || 'none';
        const memorizedHasError = !!memorized?.lastError;
        if (!memorizedHasError && this.NON_CRITICAL_ACTIONS.includes(memorizedAction)) {
          logger.debug(
            `[CrashMarker] Drapeau périmé avec action non-critique "${memorizedAction}" sans erreur → pas un crash, nettoyage`
          );
          this.clearCrashMarkers();
          return false;
        }
        return true;
      }

      const cleanShutdown = localStorage.getItem(KEYS.CLEAN_SHUTDOWN);
      const lastHeartbeat = localStorage.getItem(KEYS.HEARTBEAT);
      const sessionStart = localStorage.getItem(KEYS.SESSION_START);

      // Pas de session précédente = pas de crash
      if (!sessionStart || !lastHeartbeat) {
        return false;
      }

      // Shutdown propre = pas de crash
      if (cleanShutdown === 'true') {
        return false;
      }

      const hasError = localStorage.getItem(KEYS.LAST_ERROR);
      const lastAction = localStorage.getItem(KEYS.LAST_ACTION) || 'none';

      // ✅ FIX FAUX POSITIFS: Si "maybe" (visibilitychange/beforeunload/navigation) ET pas d'erreur → fermeture normale
      if (cleanShutdown === 'maybe' && !hasError) {
        logger.debug('[CrashMarker] Fermeture "maybe" sans erreur → pas de crash');
        return false;
      }

      // ✅ FIX FAUX POSITIFS: Si "maybe" + action non-critique → pas de crash (même avec lastError résiduel)
      // Exemples : shop_load, navigation, api_call → fermeture normale pendant un chargement
      if (cleanShutdown === 'maybe' && this.NON_CRITICAL_ACTIONS.includes(lastAction)) {
        logger.debug(`[CrashMarker] Action non-critique "${lastAction}" avec cleanShutdown=maybe → pas de crash`);
        return false;
      }

      // ✅ FIX FAUX POSITIFS: Si l'action est 'none' et pas d'erreur, c'est probablement un refresh/reload
      if ((lastAction === 'none' || !lastAction) && !hasError) {
        logger.debug('[CrashMarker] Action "none" sans erreur → pas de crash (refresh probable)');
        return false;
      }

      // ✅ FIX FAUX POSITIFS Android Median: Si action non-critique ET pas d'erreur JS,
      // c'est très probablement un kill normal de l'OS (pas un crash JS)
      // Couvre cleanShutdown='false' (visibilitychange visible puis kill OS) et cleanShutdown=null
      if (this.NON_CRITICAL_ACTIONS.includes(lastAction) && !hasError) {
        logger.debug(`[CrashMarker] Action non-critique "${lastAction}" sans erreur JS → pas de crash (kill OS probable)`);
        return false;
      }

      // Vérifier l'âge du heartbeat
      const heartbeatTime = parseInt(lastHeartbeat, 10);
      const now = Date.now();
      const timeSinceHeartbeat = now - heartbeatTime;

      // Si le heartbeat est très ancien (> 24h), c'est probablement juste un ancien boot
      if (timeSinceHeartbeat > 24 * 60 * 60 * 1000) {
        return false;
      }

      // Si pas de clean shutdown et heartbeat récent (< seuil), c'est un crash
      // Uniquement pour les actions critiques (shop_purchase, auth_change, render, role_switch)
      if (timeSinceHeartbeat < CRASH_THRESHOLD) {
        this.saveCrashContext();
        localStorage.setItem(KEYS.CRASH_DETECTED, 'true');
        return true;
      }

      return false;
    } catch {
      return false;
    }
  },

  /**
   * Sauvegarde le contexte du crash
   * ✅ Enhanced: Now includes memory and invalidation data
   */
  saveCrashContext(): void {
    try {
      const sessionStart = localStorage.getItem(KEYS.SESSION_START);
      const lastHeartbeat = localStorage.getItem(KEYS.HEARTBEAT);
      
      // Try to recover userId from Supabase session in localStorage
      let userId: string | undefined;
      let userEmail: string | undefined;
      try {
        const sbSession = Object.keys(localStorage).find(k => k.includes('auth-token'));
        if (sbSession) {
          const parsed = JSON.parse(localStorage.getItem(sbSession) || '{}');
          userId = parsed?.user?.id || parsed?.access_token ? undefined : undefined;
          userEmail = parsed?.user?.email || undefined;
          userId = parsed?.user?.id || undefined;
        }
      } catch { /* ignore */ }

      const context: CrashContext = {
        action: (localStorage.getItem(KEYS.LAST_ACTION) as CrashAction) || 'none',
        route: localStorage.getItem(KEYS.LAST_ROUTE) || 'unknown',
        timestamp: lastHeartbeat ? new Date(parseInt(lastHeartbeat, 10)).toISOString() : new Date().toISOString(),
        sessionDuration: sessionStart ? Math.floor((Date.now() - new Date(sessionStart).getTime()) / 1000) : 0,
        lastError: localStorage.getItem(KEYS.LAST_ERROR) || undefined,
        userAgent: navigator.userAgent,
        appVersion: import.meta.env.VITE_APP_VERSION || 'unknown',
        breadcrumbs: this.getBreadcrumbs(),
        // ✅ NEW: Enhanced diagnostics
        memoryMB: this.getMemoryMB(),
        peakMemoryMB: this.getPeakMemory(),
        invalidationCount: this.getInvalidationCount(),
        // ✅ User identification
        userId,
        userEmail,
      };

      localStorage.setItem(KEYS.CRASH_CONTEXT, JSON.stringify(context));
    } catch {
      // Ignore
    }
  },

  /**
   * Récupère le contexte du crash précédent
   */
  getCrashContext(): CrashContext | null {
    try {
      const contextStr = localStorage.getItem(KEYS.CRASH_CONTEXT);
      return contextStr ? JSON.parse(contextStr) : null;
    } catch {
      return null;
    }
  },

  /**
   * Efface les marqueurs de crash (après traitement)
   */
  clearCrashMarkers(): void {
    try {
      localStorage.removeItem(KEYS.CRASH_DETECTED);
      localStorage.removeItem(KEYS.CRASH_CONTEXT);
      localStorage.removeItem(KEYS.LAST_ERROR);
      logger.debug('[CrashMarker] 🧹 Marqueurs de crash effacés');
    } catch {
      // Ignore
    }
  },

  /**
   * Confirme que la page a bien été chargée
   * Appelé après le boot complet de l'app pour éviter les faux positifs
   */
  confirmPageLoaded(): void {
    try {
      const currentState = localStorage.getItem(KEYS.CLEAN_SHUTDOWN);
      // Si l'état est "maybe" (navigation/refresh en cours), le remettre à "false"
      // car la page est bien chargée maintenant
      if (currentState === 'maybe') {
        localStorage.setItem(KEYS.CLEAN_SHUTDOWN, 'false');
        logger.debug('[CrashMarker] Page loaded → cleanShutdown reset to false');
      }
    } catch {
      // Ignore
    }
  },

  /**
   * Nettoie tous les marqueurs (pour reset complet)
   */
  cleanup(): void {
    this.stopHeartbeat();
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('error', this.handleGlobalError);
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
    
    Object.values(KEYS).forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch {
        // Ignore
      }
    });
  }
};
